import { useLogger } from '@ori/logger';
import type { Dispatch, FC, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { useGetSearchPreviewData } from '../../hooks/useGetSearchPreviewData';
import type { SearchPreviewResult } from '../../utils';
import { useAppConfig } from '../configurationContext';

export interface SearchInputContext {
  blur: () => void;
  clearResults: () => void;
  focus: () => void;
  hide: () => void;
  isFocused: boolean;
  isVisible: boolean;
  results?: SearchPreviewResult | undefined;
  search: (query: string) => void;
  setValue: Dispatch<SetStateAction<string>>;
  show: () => void;
  timestamp: number;
  value: string;
  debouncedValue: string;
  manualShow: () => void;
  manualHide: () => void;
  isManuallyOpened: boolean;
  isLoading: boolean;
}

export const searchInputContext = createContext<SearchInputContext | undefined>(undefined);

const { Provider } = searchInputContext;

export const SearchInputProvider: FC<PropsWithChildren> = ({ children }) => {
  const logger = useLogger('SearchInputProvider');
  const { showSearchOnScroll } = useAppConfig();
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(Boolean(showSearchOnScroll));
  const [isManuallyOpened, setIsManuallyOpened] = useState(false);
  const [results, setResults] = useState<SearchPreviewResult | undefined>(undefined);
  const [timestamp, setTimestamp] = useState(0);
  const [value, setValue] = useState('');
  const blur = useCallback(() => setIsFocused(false), []);
  const focus = useCallback(() => setIsFocused(true), []);
  const hide = useCallback(() => setIsVisible(false), []);
  const show = useCallback(() => setIsVisible(true), []);
  const manualShow = useCallback(() => setIsManuallyOpened(true), []);
  const manualHide = useCallback(() => setIsManuallyOpened(false), []);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedValue] = useDebounce(value, 750);

  const { getData } = useGetSearchPreviewData();

  const clearResults = useCallback(() => setResults(undefined), []);
  const search = useCallback(
    (query: string) => {
      if (!query) {
        return;
      }
      setIsLoading(true);
      getData(query)
        .then((data) => {
          setResults(data);
          setTimestamp(Date.now());
          setIsLoading(false);
        })
        .catch((error) => logger.error(new Error('Cannot get suggestions as request failed', { cause: error })));
    },
    [getData, logger],
  );

  useEffect(() => {
    search(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps  -- we want to fetch results ONLY when input value changes (with debounce)
  }, [debouncedValue]);

  const context = useMemo(
    () => ({
      blur,
      clearResults,
      focus,
      hide,
      isFocused,
      isManuallyOpened,
      isVisible,
      manualHide,
      manualShow,
      results,
      search,
      setValue,
      show,
      timestamp,
      value,
      isLoading,
      debouncedValue,
    }),
    [
      blur,
      clearResults,
      focus,
      hide,
      isFocused,
      isManuallyOpened,
      isVisible,
      manualHide,
      manualShow,
      results,
      search,
      show,
      timestamp,
      value,
      isLoading,
      debouncedValue,
    ],
  );

  return <Provider value={context}>{children}</Provider>;
};

export const useSearchInput = () => {
  const context = useContext(searchInputContext);
  if (context) {
    return context;
  }
  throw new Error('useSearchInput must be used within a SearchInputProvider.');
};
